import { connect } from 'react-redux';
import deepmerge from 'deepmerge';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { alert } from 'components/Alert';
import Button from 'components/Button';

import Switcher from 'pages/Configurators/components/Switcher';

import {
  fetchProfile,
  submitChangedContactName,
  submitChangedPayPal,
  submitChangedProfile,
  submitChangedSocialMediaLink,
  submitDelete,
} from 'store/actions/userData';

import ErrorsWrapper from 'styles/ErrorsWrapper';
import SuccessWrapper from 'styles/SuccessWrapper';

import { hop, PersonalDataValidator } from 'utils';
import { isObject } from 'utils/validators';
import logout from 'utils/logout';

import Heading from '../../../../styles/Heading';
import StyledAccountSettingsDropDownContent from '../../../../styles/StyledAcountSettingsDropDownContent';

import AddressAutocomplete from './components/AddressAutocomplete';
import BankTransferValidationStatus from './components/BankTransferValidationStatus';
import CountrySelect from './components/CountrySelect';
import FramedText from './components/FramedText';
import PayPalButton from './components/PayPalButton';
import Title from './components/Title';
import UnverifiedUserMessage from './components/UnverifiedUserMessage';

import AlertText from './styles/AlertText';
import DeleteButton from './styles/DeleteButton';
import Input from './styles/Input';

function snakeToCamel(string) {
  return string.replace(/(_\w)/g, m => m[1].toUpperCase());
}

class PersonalData extends Component {
  static propTypes = {
    dispatchSubmitChangedProfile: PropTypes.instanceOf(Function).isRequired,
    dispatchSubmitChangedSocialMediaLink: PropTypes.instanceOf(Function).isRequired,
    dispatchSubmitChangedContactName: PropTypes.instanceOf(Function).isRequired,
    dispatchSubmitDelete: PropTypes.instanceOf(Function).isRequired,
    dispatchFetchProfile: PropTypes.instanceOf(Function).isRequired,
    profile: PropTypes.instanceOf(Object).isRequired,
    userData: PropTypes.instanceOf(Object).isRequired,
  };

  constructor(props) {
    super(props);

    const { profile } = props;
    let fieldsData = profile;

    if (profile.pending) {
      fieldsData = profile.pending;
    }

    this.state = {
      fields: {
        fullname: fieldsData.fullname,
        personal_number: fieldsData.personal_number,
        address: {
          street: fieldsData.address.street,
          house_number: fieldsData.address.house_number || fieldsData.address.houseNumber || '',
          apartment_number:
            fieldsData.address.apartment_number || fieldsData.address.apartmentNumber || '',
          city: fieldsData.address.city,
          postal_code: fieldsData.address.postal_code,
          country: fieldsData.address.country,
        },
        bank_number: fieldsData.bank_number || '',
        paypal_email: fieldsData.paypal_email || '',
        social_media_link: fieldsData.social_media_link || '',
        is_company: fieldsData.is_company || false,
        contact_name: fieldsData.contact_name || '',
        contact_email: fieldsData.contact_email || '',
      },
      loading: false,
      deleteAccountPassword: '',
      fieldsErrors: {},
      fieldsSuccess: {},
      fullNameNeedConfirm: false,
      fullNameConfirmError: false,
      fullNameConfirm: '',
      validatedNipError: false,
      validatedFullnameError: false,
      socialMediaLinkFatalError: false,
      countrySelectOpen: false,
      nip24Snapshot: null,
      nip24Warning: null,
      requiresApartment: false,
      companyNameLocked: !!(fieldsData.is_company && fieldsData.fullname),
      softValidationWarnings: [],
      softDataNeedConfirm: false,
      softDataConfirmed: false,
    };

    this.firstErrorRef = React.createRef();
    this.validator = new PersonalDataValidator();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.firstErrorRef.current) {
      const targetField = this.firstErrorRef.current.previousSibling;
      targetField.focus({ preventScroll: true });
      targetField.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
      this.firstErrorRef.current = null;
    }
  }

  /**
   * Funkcja aktualizująca stan dla loadera
   *
   * @param {boolean} value
   * @returns {void}
   */
  setLoaderState = value => {
    this.setState({
      loading: value,
    });
  };

  /**
   * Funkcja aktualizująca wartości w inputach.
   *
   * @param {Event} event
   * @param {string} nested
   */
  onChangeInput = (event, nested) => {
    let updatedValue = { [event.target.name]: event.target.value };

    if (nested) {
      updatedValue = { [nested]: updatedValue };
    }

    this.setState(prevState => ({
      fields: deepmerge(prevState.fields, updatedValue),
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
    }));
  };

  onChangeSelect = (value, key) => {
    const updatedValue = { address: { [key]: value } };

    this.setState(prevState => ({
      fields: deepmerge(prevState.fields, updatedValue),
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
    }));
  };

  digitsOnly = nip => nip.replace(/\D/g, '');

  onNipBlur = async () => {
    const { fields } = this.state;
    if (!fields.is_company) {
      return;
    }
    const nip = this.digitsOnly(fields.personal_number);
    if (nip.length !== 10) {
      return;
    }
    try {
      const data = await this.validator.lookupNip(nip);
      if (data.status === 'invalid' || data.valid === false) {
        this.setState({
          validatedNipError: true,
          nip24Warning: data.message || 'Nie znaleziono firmy o podanym NIP.',
        });
        return;
      }
      if (data.status === 'unavailable') {
        this.setState({
          validatedNipError: false,
          nip24Warning:
            data.message ||
            'Rejestr NIP24 chwilowo niedostępny — dane możesz wysłać do weryfikacji ręcznej.',
        });
        return;
      }
      const companyName = data.companyName || (data.data && data.data.companyName) || '';
      const address = data.address || (data.data && data.data.address) || {};
      this.setState(prevState => ({
        validatedNipError: false,
        nip24Warning: null,
        companyNameLocked: !!companyName,
        nip24Snapshot: {
          companyName,
          address,
          nip,
        },
        fields: {
          ...prevState.fields,
          fullname: companyName || prevState.fields.fullname,
          address: {
            ...prevState.fields.address,
            street: address.street || prevState.fields.address.street,
            house_number: address.houseNumber || prevState.fields.address.house_number,
            apartment_number: address.apartmentNumber || prevState.fields.address.apartment_number,
            city: address.city || prevState.fields.address.city,
            postal_code: address.postalCode || prevState.fields.address.postal_code,
            country: address.country || prevState.fields.address.country || 'PL',
          },
        },
      }));
    } catch (e) {
      this.setState({
        nip24Warning:
          'Nie udało się pobrać danych firmy. Możesz kontynuować — sprawdzimy przy wysyłce.',
      });
    }
  };

  onPlaceSelected = (_details, meta = {}) => {
    const type = meta.type || 'street';
    const suggestionLabel = (meta.suggestion && meta.suggestion.description) || '';
    if (!suggestionLabel) {
      return;
    }

    this.setState(prevState => {
      const prev = prevState.fields.address;
      const softReset = {
        softDataConfirmed: false,
        softDataNeedConfirm: false,
        softValidationWarnings: [],
      };

      // Selection updates only the focused field (+ clears fields below). Never touch others.
      if (type === 'city') {
        return {
          ...softReset,
          requiresApartment: false,
          fields: {
            ...prevState.fields,
            address: {
              ...prev,
              city: suggestionLabel,
              street: '',
              house_number: '',
              apartment_number: '',
            },
          },
        };
      }

      // street
      return {
        ...softReset,
        requiresApartment: false,
        fields: {
          ...prevState.fields,
          address: {
            ...prev,
            street: suggestionLabel,
            house_number: '',
            apartment_number: '',
          },
        },
      };
    });
  };

  /** Cascade: changing a field clears all address fields below it. */
  onPostalChange = event => {
    const next = event.target.value;
    this.setState(prevState => ({
      requiresApartment: false,
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
      fields: {
        ...prevState.fields,
        address: {
          ...prevState.fields.address,
          postal_code: next,
          city: '',
          street: '',
          house_number: '',
          apartment_number: '',
        },
      },
    }));
  };

  onCityChange = event => {
    const next = event.target.value;
    this.setState(prevState => ({
      requiresApartment: false,
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
      fields: {
        ...prevState.fields,
        address: {
          ...prevState.fields.address,
          city: next,
          street: '',
          house_number: '',
          apartment_number: '',
        },
      },
    }));
  };

  onStreetChange = event => {
    const next = event.target.value;
    this.setState(prevState => ({
      requiresApartment: false,
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
      fields: {
        ...prevState.fields,
        address: {
          ...prevState.fields.address,
          street: next,
          house_number: '',
          apartment_number: '',
        },
      },
    }));
  };

  onHouseChange = event => {
    const next = event.target.value;
    this.setState(prevState => ({
      requiresApartment: false,
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
      fields: {
        ...prevState.fields,
        address: {
          ...prevState.fields.address,
          house_number: next,
          apartment_number: '',
        },
      },
    }));
  };

  /**
   * Funkcja aktualizująca wartości w SocialMediaLink patch action.
   *
   * @param {Event} event
   * @param {string} nested
   */
  onChangeSocialMediaLink = (event, nested) => {
    const updatedValue = { [event.target.name]: event.target.value };

    this.setState(prevState => ({
      fields: deepmerge(prevState.fields, updatedValue),
    }));
  };

  onFullNameChange = event => {
    const currentValue = event.target.value;
    const { profile } = this.props;

    if (currentValue !== profile.fullname) {
      this.setState({
        fullNameNeedConfirm: true,
        fullNameConfirmError: false,
        fullNameConfirm: '',
      });
    }

    this.setState(prevState => ({
      softDataConfirmed: false,
      softDataNeedConfirm: false,
      softValidationWarnings: [],
      fields: {
        ...prevState.fields,
        fullname: currentValue,
      },
    }));
  };

  onFullNameConfirmChange = event => {
    const currentValue = event.target.value;
    const { fields } = this.state;

    this.setState({
      fullNameConfirmError: currentValue !== fields.fullname,
      fullNameConfirm: currentValue,
    });
  };

  /**
   * Funkcja do wysyłki formularza z danymi osobowymi
   *
   * @param {Event} event
   */
  onSubmit = async event => {
    event.preventDefault();
    let errors = {};
    let socialMediaLinkFatalError = false;
    let validatedNipError = false;
    let validatedFullnameError = false;
    let validatedBankError = false;
    const softWarnings = [];
    const pushSoft = message => {
      if (message && !softWarnings.includes(message)) {
        softWarnings.push(message);
      }
    };

    this.setState({
      socialMediaLinkFatalError: false,
      validatedNipError: false,
      validatedFullnameError: false,
      validatedBankError: false,
    });
    const {
      dispatchFetchProfile,
      dispatchSubmitChangedProfile,
      dispatchSubmitChangedSocialMediaLink,
      dispatchSubmitChangedContactName,
      profile,
    } = this.props;
    const {
      fields,
      fullNameNeedConfirm,
      fullNameConfirmError,
      fullNameConfirm,
      companyNameLocked,
      softDataConfirmed,
      softDataNeedConfirm,
    } = this.state;
    const profileFields = {
      ...fields,
    };

    this.setLoaderState(true);

    if (!profile.fullname_locked && !(fields.is_company && companyNameLocked)) {
      const fullnameDetails = await this.validator.fullnameDetails(fields.fullname);

      if (!fullnameDetails.valid) {
        validatedFullnameError = true;
        this.setState({
          validatedFullnameError: true,
        });
      } else if (fullnameDetails.status === 'warning') {
        pushSoft(
          fullnameDetails.message ||
            'Imię i nazwisko nie zostało w pełni potwierdzone. Sprawdź poprawność danych.',
        );
      }
    }

    const validatedSocialMediaLink = await this.validator.socialMediaUrl(fields.social_media_link);

    if (!this.isProfileChanged(profileFields, profile) && validatedSocialMediaLink) {
      await dispatchSubmitChangedSocialMediaLink({ socialMediaLink: fields.social_media_link });
    }

    if (!validatedSocialMediaLink) {
      socialMediaLinkFatalError = true;
      this.setState({
        socialMediaLinkFatalError: true,
      });
    }

    if (!this.isProfileChanged(profileFields, profile)) {
      await dispatchSubmitChangedContactName({
        contactName: fields.contact_name,
        contactEmail: fields.contact_email,
      });
    }

    if (fields.is_company) {
      const nipDetails = await this.validator.nipDetails(fields.personal_number);
      if (!nipDetails.valid) {
        validatedNipError = true;
        this.setState({
          validatedNipError: true,
        });
      } else if (nipDetails.status === 'warning') {
        pushSoft(
          nipDetails.message ||
            'NIP nie został potwierdzony w rejestrze. Sprawdź poprawność danych.',
        );
      }
    }

    const validatedBankNumber = await this.validator.bankNumber(fields.bank_number);
    const streetPayload = {
      country: fields.address.country,
      street: fields.address.street,
      houseNumber: fields.address.house_number,
      apartmentNumber: fields.address.apartment_number,
      city: fields.address.city,
      postalCode: fields.address.postal_code,
    };
    const streetDetails = await this.validator.streetDetails(streetPayload);
    const missingApartment = !!streetDetails.requiresApartment && !fields.address.apartment_number;
    if (missingApartment) {
      this.setState({ requiresApartment: true });
      pushSoft(
        streetDetails.message ||
          'Dla tego adresu wymagany jest numer mieszkania — upewnij się, że dane są poprawne.',
      );
    } else if (
      (!streetDetails.valid ||
        streetDetails.status === 'invalid' ||
        streetDetails.status === 'warning') &&
      fields.address.street
    ) {
      pushSoft(
        streetDetails.message ||
          'Adres (ulica / numer) nie został potwierdzony. Sprawdź poprawność danych.',
      );
    }

    if (!validatedBankNumber && fields.bank_number) {
      validatedBankError = true;
      this.setState({
        validatedBankError: true,
      });
    }

    if (fields.bank_number.length === 0 && profile.bank_number) {
      validatedBankError = true;
      this.setState({
        validatedBankError: true,
      });
    }

    let normalizedPostalCode = fields.address.postal_code.trim();
    normalizedPostalCode = normalizedPostalCode.toUpperCase();
    fields.address.postal_code = normalizedPostalCode;
    normalizedPostalCode = fields.address.postal_code.replace(/ /g, '');

    const validatedPostalCode = await this.validator.postalCode({
      country: fields.address.country,
      postalCode: normalizedPostalCode,
    });
    if (!validatedPostalCode && fields.address.postal_code) {
      pushSoft('Kod pocztowy nie został potwierdzony. Sprawdź poprawność danych.');
    }

    const fullNameCorrectlyChanged =
      !fullNameNeedConfirm ||
      (fullNameNeedConfirm && !fullNameConfirmError && fullNameConfirm === fields.fullname);

    if (!fullNameCorrectlyChanged) {
      this.setState({
        fullNameConfirmError: true,
      });
    }

    const hardChecksOk =
      !profile.pending &&
      !socialMediaLinkFatalError &&
      !validatedNipError &&
      !validatedFullnameError &&
      !validatedBankError &&
      fullNameCorrectlyChanged;

    // Soft failures: ask user to confirm data are correct before save
    if (softWarnings.length > 0 && !softDataConfirmed) {
      this.setState({
        softValidationWarnings: softWarnings,
        softDataNeedConfirm: true,
        fieldsErrors: errors,
      });
      if (hardChecksOk) {
        this.setLoaderState(false);
        return;
      }
    }

    if (softWarnings.length === 0 && softDataNeedConfirm) {
      this.setState({
        softValidationWarnings: [],
        softDataNeedConfirm: false,
        softDataConfirmed: false,
      });
    }

    if (hardChecksOk && this.isProfileChanged(profileFields, profile)) {
      if (!!profile.paypal_email || fields.bank_number.length) {
        try {
          const { nip24Snapshot } = this.state;
          const updated = {
            ...profileFields,
          };

          updated.socialMediaLink = fields.social_media_link;
          updated.bankNumber = fields.bank_number.toUpperCase();
          updated.personalNumber = fields.personal_number;
          updated.isCompany = fields.is_company;
          updated.address = {
            street: fields.address.street,
            houseNumber: fields.address.house_number || null,
            apartmentNumber: fields.address.apartment_number || null,
            city: fields.address.city,
            postalCode: fields.address.postal_code,
            country: fields.address.country,
          };
          updated.contactEmail = fields.contact_email;
          updated.contactName = fields.contact_name;
          if (nip24Snapshot) {
            updated.nip24Snapshot = nip24Snapshot;
          }

          delete updated.social_media_link;
          delete updated.bank_number;
          delete updated.personal_number;
          delete updated.is_company;
          delete updated.contact_email;
          delete updated.contact_name;

          await dispatchSubmitChangedProfile(updated);

          this.setState({
            softValidationWarnings: [],
            softDataNeedConfirm: false,
            softDataConfirmed: false,
          });

          alert.open({
            config: {
              type: 'loading',
              title: 'Sukces',
              description: (
                <span>Dane osobowe zostały poprawnie zmienione. Teraz czekają na akceptację.</span>
              ),
              confirmButtonText: 'Rozumiem',
            },
          });

          dispatchFetchProfile();
        } catch (error) {
          if (error.response.status === 400) {
            errors = {
              ...errors,
              ...error.response.data,
            };

            this.setState({ fieldsErrors: errors });
          } else {
            alert.open({
              config: {
                type: 'error',
                title: 'Wystąpił bład',
                description: <span>{error.response.data.message}</span>,
                confirmButtonText: 'Innym razem',
              },
            });
          }
        }
      } else {
        errors.bankNumber = {
          errors: ['Wymagany jest Numer konta bankowego lub połączenie z kontem PayPal.'],
        };
      }
    }

    this.setState({ fieldsErrors: errors });
    this.setLoaderState(false);
  };

  isProfileChanged = (fields, profile) => {
    let changed = false;

    for (const fieldName in fields) {
      if (hop(fields, fieldName)) {
        const isProfileField = hop(profile, fieldName);

        if (!isProfileField) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const isNested = isObject(fields[fieldName]);

        if (isNested) {
          if (this.isProfileChanged(fields[fieldName], profile[fieldName])) {
            return true;
          }
        } else if (fields[fieldName] !== profile[fieldName]) {
          if (
            fieldName === 'social_media_link' ||
            fieldName === 'contact_name' ||
            fieldName === 'contact_email'
          ) {
            // eslint-disable-next-line no-self-assign
            changed = changed;
          } else if (fieldName === 'bank_number') {
            if (
              fields[fieldName].toUpperCase().replace('PL', '') !==
              profile[fieldName].replace('PL', '')
            ) {
              changed = true;
            }
          } else {
            changed = true;
          }
        }
      }
    }
    return changed;
  };

  /**
   * Funkcja do usuwania konta po potwierdzeniu hasła
   */
  onDeleteSubmit = async event => {
    const { dispatchSubmitDelete } = this.props;
    const { deleteAccountPassword } = this.state;

    event.preventDefault();

    this.setState(
      {
        loading: true,
      },
      () => alert.update(this.getDeleteConfirmAlertConfig()),
    );

    try {
      await dispatchSubmitDelete({ current_password: deleteAccountPassword });
      this.setState({ fieldsErrors: {} });
      logout();
    } catch (error) {
      if (error.response.status === 400) {
        this.setState(
          {
            fieldsErrors: {
              currentPassword: {
                errors: ['Musisz podać poprawne hasło do swojego konta'],
              },
            },
            loading: false,
          },
          () => alert.update(this.getDeleteConfirmAlertConfig()),
        );
      } else {
        alert.open({
          config: {
            type: 'error',
            title: 'Wystąpił bład',
            description: <span>{error.response.data.message}</span>,
            confirmButtonText: 'Innym razem',
          },
        });
      }
    }

    this.setLoaderState(false);
  };

  getDeleteConfirmAlertConfig = () => {
    const { loading } = this.state;

    return {
      config: {
        type: 'error',
        title: 'Czy na pewno?',
        description: (
          <span>
            Usunięcie konta to ogromna strata.
            <br />
            <br />
            Wraz z usunięciem konta, anulowane zostaną wszelkie zlecenia, działania oraz dane
            przypisane do Twojego konta.
            <br />
            <br />
            Potwierdź swoją decyzję wpisując swoje hasło.
            <br />
            <br />
            <form onSubmit={this.onDeleteSubmit}>
              <Input
                onChange={event => {
                  this.setState({ deleteAccountPassword: event.target.value });
                }}
                placeholder="Twoje hasło"
                type="password"
              />
              {this.renderErrors('currentPassword')}
              <Button
                noWrapper
                block
                background="red"
                loading={loading}
                onClick={this.onDeleteSubmit}
              >
                Usuwamy
              </Button>
            </form>
          </span>
        ),
        confirmButton: false,
        cancelButtonText: 'Jednak nie, żartowałem',
        cancelButton: true,
      },
    };
  };

  /**
   * Rekurencyjna funkcja wyszukująca błędy w zwracanych obiektach
   *
   * @param {Object} fieldsErrors
   * @param {string} fieldName
   * @param {Object} errors
   *
   * @returns {Object}
   */
  findErrors = (fieldsErrors, fieldName, errors = {}) => {
    const keys = Object.keys(fieldsErrors);

    // Jeżeli trafiliśmy na klucz to zwracamy znalezioną wartość klucza
    if (keys.includes(fieldName)) {
      return fieldsErrors[fieldName];
    }

    // Iterujemy po każdym kluczu i wyszukujemy dalej
    let foundErrors = {};
    for (const key in fieldsErrors) {
      if (Object.prototype.hasOwnProperty.call(fieldsErrors, key)) {
        if (fieldsErrors[key] === Object(fieldsErrors[key])) {
          foundErrors = this.findErrors(fieldsErrors[key], fieldName, errors);
        }
      }
    }

    return foundErrors;
  };

  /**
   * Funkcja renderująca błedy dla pola przekazanego w argumencie
   *
   * @param {string} fieldName
   * @returns {(Node|null)}
   */
  renderErrors = fieldName => {
    const { fieldsErrors } = this.state;
    const findErrors = this.findErrors(fieldsErrors, snakeToCamel(fieldName));

    // Sprawdzamy czy obiekt posiada klucz "errors" oraz czy klucz "errors" posiada wartość która jest tablicą
    if (!findErrors.errors && !(findErrors.errors instanceof Array)) return null;

    return (
      <ErrorsWrapper
        ref={this.firstErrorRef.current ? {} : this.firstErrorRef} // assign first reference if none exists
        style={{ margin: '0 0 15px' }}
      >
        {findErrors.errors.map(error => (
          <li key={error}>{error}</li>
        ))}
      </ErrorsWrapper>
    );
  };

  /**
   * Funkcja renderująca błedy dla pola przekazanego w argumencie
   *
   * @param {string} fieldName
   * @returns {(Node|null)}
   */
  renderSuccess = fieldName => {
    const { fieldsSuccess } = this.state;
    const success = hop(fieldsSuccess, fieldName) ? fieldsSuccess[fieldName] : null;

    if (success === null) return null;

    return (
      <SuccessWrapper style={{ margin: '0 0 15px' }}>
        <li>{success}</li>
      </SuccessWrapper>
    );
  };

  openCountrySelect = () => {
    this.setState({ countrySelectOpen: true });
  };

  closeCountrySelect = () => {
    this.setState({ countrySelectOpen: false });
  };

  render() {
    const {
      fields,
      loading,
      fullNameNeedConfirm,
      fullNameConfirmError,
      socialMediaLinkFatalError,
      validatedNipError,
      validatedFullnameError,
      validatedBankError,
      countrySelectOpen,
      nip24Warning,
      companyNameLocked,
      requiresApartment,
      softValidationWarnings,
      softDataNeedConfirm,
      softDataConfirmed,
    } = this.state;
    const { profile, userData } = this.props;

    return (
      <StyledAccountSettingsDropDownContent>
        <Heading>Dane osobowe</Heading>

        {userData.missing_personal_data && !profile.pending ? (
          <UnverifiedUserMessage />
        ) : (
          <form onSubmit={this.onSubmit} noValidate autoComplete="off">
            <BankTransferValidationStatus />
            <div className="form-group">
              <Switcher
                text={`${profile.is_company ? ' Aktywne konto Firmowe' : ' Aktywuj konto Firmowe'}`}
                name="is_company"
                value={profile.is_company}
                onChange={() => {}}
              />
            </div>
            <FramedText
              text="Jeżeli chcesz zmienić typ konta na firmowe lub prywatne napisz do nas."
              theme="gray"
            />
            <div className="form-group">
              {profile.is_company && <Title>Nazwa firmy</Title>}
              {!profile.is_company && <Title>Jak się nazywasz?</Title>}
              {!!profile.pending && (
                <FramedText
                  color="white"
                  text="Dane są w trakcie weryfikacji przez administratora serwisu. W miedzyczasie nie możesz edytować wpisanych danych. Weryfikacja trwa zazwyczaj do 24 godzin."
                  theme="red"
                />
              )}
              {profile.is_company && (
                <Input
                  readOnly={!!profile.pending || profile.fullname_locked}
                  value={this.digitsOnly(fields.personal_number)}
                  onChange={event => {
                    this.onChangeInput(event);
                  }}
                  onBlur={this.onNipBlur}
                  name="personal_number"
                  placeholder="NIP"
                  title="Numer NIP"
                  type="text"
                  style={
                    profile.fullname_locked ? { backgroundColor: 'var(--bg-color-white-gray)' } : {}
                  }
                  required
                />
              )}
              {validatedNipError && fields.is_company && (
                <FramedText text="NIP wpisany niepoprawnie." theme="red" />
              )}
              {nip24Warning && fields.is_company && (
                <FramedText text={nip24Warning} theme="orange" />
              )}
              {this.renderErrors('personal_number')}
              <Input
                readOnly={
                  !!profile.pending ||
                  profile.fullname_locked ||
                  (fields.is_company && companyNameLocked)
                }
                value={fields.fullname}
                onChange={event => this.onFullNameChange(event)}
                name="fullname"
                placeholder={
                  fields.is_company ? 'Nazwa firmy (z rejestru NIP)*' : 'Imię i Nazwisko*'
                }
                type="text"
                style={
                  profile.fullname_locked || (fields.is_company && companyNameLocked)
                    ? { backgroundColor: 'var(--bg-color-white-gray)' }
                    : {}
                }
                required
              />
              {this.renderErrors('fullname')}
              {validatedFullnameError && fields.fullname && (
                <FramedText text="Wpisz imię oraz nazwisko" theme="red" />
              )}
              {(profile.fullname_locked || (fields.is_company && companyNameLocked)) && (
                <p style={{ margin: '-5px 0 20px' }}>
                  {fields.is_company
                    ? 'Nazwa firmy pobierana jest automatycznie z rejestru NIP i nie podlega edycji.'
                    : `Nie możesz zmienić imienia i nazwiska. W sytuacjach wyjątkowych napisz na `}
                  {!fields.is_company && (
                    <a href="mailto:ksiegowosc@tipply.pl">ksiegowosc@tipply.pl</a>
                  )}
                </p>
              )}
              {fullNameNeedConfirm && !(fields.is_company && companyNameLocked) && (
                <>
                  <p style={{ margin: '-5px 0 10px' }}>
                    <strong>Uwaga!</strong>
                    {` Wpisanie imienia i nazwiska w serwisie jest nieodwracalne. Nie ma możliwości jego
                  zmiany, bądź edycji w przyszłości.`}
                  </p>
                  <Input
                    onChange={event => this.onFullNameConfirmChange(event)}
                    style={{ marginBottom: 30 }}
                    placeholder="Potwierdź Imię i Nazwisko"
                  />
                  {fullNameConfirmError && (
                    <ErrorsWrapper style={{ margin: '-15px 0 30px' }}>
                      <li>
                        W pierwszej i drugiej rubryce należy wpisać takie samo imię i nazwisko w
                        celu potwierdzenia. Zweryfikuj to i spróbuj ponownie.
                      </li>
                    </ErrorsWrapper>
                  )}
                </>
              )}
            </div>

            <div className="form-group">
              <Title>Gdzie streamujesz lub tworzysz swoje treści w internecie?</Title>
              <Input
                readOnly={!!profile.pending}
                value={fields.social_media_link}
                onChange={event => this.onChangeInput(event)}
                name="social_media_link"
                placeholder="Social media link"
                type="text"
              />
              {socialMediaLinkFatalError && <FramedText text="Nieprawidłowy link." theme="red" />}
              {this.renderErrors('socialMediaLink')}
            </div>

            <div className="form-group">
              <Title>Gdzie mieszkasz?</Title>

              <CountrySelect
                value={fields.address.country}
                readOnly={!!profile.pending}
                menuIsOpen={countrySelectOpen}
                onMenuOpen={this.openCountrySelect}
                onMenuClose={this.closeCountrySelect}
                onChange={value => {
                  this.onChangeSelect(value, 'country');
                  setTimeout(this.closeCountrySelect, 100);
                }}
              />
              {this.renderErrors('country')}

              <Input
                readOnly={!!profile.pending}
                value={fields.address.postal_code}
                onChange={this.onPostalChange}
                name="postal_code"
                placeholder="Kod pocztowy 00-000*"
                type="text"
                required
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              {this.renderErrors('postalCode')}

              <AddressAutocomplete
                value={fields.address.city}
                readOnly={!!profile.pending}
                disabled={
                  !fields.address.postal_code || fields.address.postal_code.trim().length < 2
                }
                country={fields.address.country}
                type="city"
                postalCode={fields.address.postal_code}
                name="city"
                onChange={this.onCityChange}
                onPlaceSelected={this.onPlaceSelected}
                placeholder={
                  fields.address.postal_code && fields.address.postal_code.trim().length >= 2
                    ? 'Miasto*'
                    : 'Najpierw podaj kod pocztowy'
                }
              />
              {this.renderErrors('city')}

              <AddressAutocomplete
                value={fields.address.street}
                readOnly={!!profile.pending}
                disabled={!fields.address.city || fields.address.city.trim().length < 2}
                country={fields.address.country}
                type="street"
                postalCode={fields.address.postal_code}
                city={fields.address.city}
                name="street"
                onChange={this.onStreetChange}
                onPlaceSelected={this.onPlaceSelected}
                placeholder={
                  fields.address.city && fields.address.city.trim().length >= 2
                    ? 'Ulica*'
                    : 'Najpierw podaj miasto'
                }
              />
              {this.renderErrors('street')}

              <Input
                readOnly={!!profile.pending}
                value={fields.address.house_number}
                onChange={this.onHouseChange}
                name="house_number"
                placeholder="Numer domu*"
                type="text"
                required
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              {this.renderErrors('houseNumber')}

              <Input
                readOnly={!!profile.pending}
                value={fields.address.apartment_number}
                onChange={event => this.onChangeInput(event, 'address')}
                name="apartment_number"
                placeholder={
                  requiresApartment ? 'Numer mieszkania*' : 'Numer mieszkania (opcjonalnie)'
                }
                type="text"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              {this.renderErrors('apartmentNumber')}

              <FramedText
                text={`Pamiętaj o podaniu numeru domu. W Twojej miejscowości nie ma nazwy
                ulicy? Wprowadź w <b>Ulica</b> nazwę miejscowości i numer domu, a w <b>Miasto</b> nazwę gminy.`}
                theme="orange"
              />
            </div>
            <div className="form-group">
              <Title>Gdzie wypłacamy zgromadzone środki?</Title>
              <Input
                readOnly={!!profile.pending}
                value={fields.bank_number}
                onChange={event => this.onChangeInput(event)}
                name="bank_number"
                placeholder="Numer konta bankowego"
                type="text"
              />
              {this.renderErrors('bankNumber')}

              <FramedText
                text={`${
                  userData.validated_with_bank_transfer
                    ? '<b>Uwaga:</b> Zmiana numeru konta bankowego będzie wymagała ponownej weryfikacji konta przelewem.<br><br>'
                    : ''
                }Akceptujemy tylko polskie konta bankowe, a przedrostek "PL" dodajemy automatycznie.`}
                theme="gray"
              />

              <PayPalButton />

              <FramedText
                text='<b>Uwaga:</b> Na email PayPal który zostanie wpisany w tej rubryce, będą realizowane bezpośrednie
                transakcje PayPal, które będą umożliwiały płatność PayPal widzom. Transakcje PayPal są bezpośrednie, a
                więc nie trafiają do bilansów konta Tipply.
                <br><br>
                Na podany e-mail będą również realizowane wypłaty PayPal zlecone w panelu wypłat oraz sam e-mail
                zostanie udostępniony widzom w celu poprawnego podpisania Umowy z Widzem.
                <br><br>
                Pamiętaj o tym, że PayPal pobiera od transakcji swoją opłatę w wysokości 2,9% oraz 1,35zł opłaty stałej,
                pobieranej co transakcję, szczegóły:
                <a href="https://www.paypal.com/pl/webapps/mpp/paypal-fees" target="_blank"><b>PayPal</b></a>'
                theme="gray"
              />
            </div>

            <div className="form-group">
              <Title>Jak widzowie mogą się z Tobą skontaktować?</Title>
              <Input
                readOnly={!!profile.pending}
                value={fields.contact_name}
                onChange={event => this.onChangeInput(event)}
                name="contact_name"
                placeholder="Imię Nazwisko i/lub nazwa firmy"
                type="text"
              />

              <Input
                readOnly={!!profile.pending}
                value={fields.contact_email}
                onChange={event => this.onChangeInput(event)}
                name="contact_email"
                placeholder="Adres email do kontaktu ze mną"
                type="email"
              />

              <FramedText
                text="Rubryki te będą udostępnione widzowi, jeżeli chciałby np. zgłosić reklamację do wysłanej
                wiadomości. Dane będą widoczne w Regulaminie Twojej strony wpłat."
                theme="gray"
              />
            </div>

            {socialMediaLinkFatalError && <FramedText text="Nieprawidłowy link." theme="red" />}
            {validatedNipError && fields.is_company && (
              <FramedText text="NIP wpisany niepoprawnie." theme="red" />
            )}
            {validatedFullnameError && fields.fullname && (
              <FramedText text="Wpisz imię oraz nazwisko" theme="red" />
            )}
            {fullNameConfirmError && (
              <ErrorsWrapper style={{ margin: '-15px 0 30px' }}>
                <li>
                  W pierwszej i drugiej rubryce należy wpisać takie samo imię i nazwisko lub dane
                  firmy w celu potwierdzenia. Zweryfikuj to i spróbuj ponownie.
                </li>
              </ErrorsWrapper>
            )}
            {validatedBankError && (
              <FramedText text="Nieprawidłowy numer konta bankowego." theme="red" />
            )}
            {softDataNeedConfirm && softValidationWarnings.length > 0 && (
              <>
                <FramedText
                  theme="orange"
                  text={`Nie udało się w pełni zweryfikować niektórych danych:<br>${softValidationWarnings
                    .map(warning => `• ${warning}`)
                    .join(
                      '<br>',
                    )}<br><br>Jeśli jesteś pewien/pewna, że dane są poprawne, potwierdź poniżej i zapisz ponownie.`}
                />
                <label
                  htmlFor="soft-data-confirm"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    margin: '0 0 20px',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                >
                  <input
                    id="soft-data-confirm"
                    type="checkbox"
                    checked={softDataConfirmed}
                    onChange={event => this.setState({ softDataConfirmed: event.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  <span>Potwierdzam, że podane dane osobowe są poprawne</span>
                </label>
              </>
            )}
            {!profile.pending && (
              <Button
                loading={loading}
                type="submit"
                noWrapper
                block
                background="blue"
                disabled={loading || (softDataNeedConfirm && !softDataConfirmed)}
              >
                Zapisz zmiany
              </Button>
            )}
          </form>
        )}

        <AlertText>
          Klikając przycisk “usuń konto” Twoje konto oraz dane osobowe zostają trwale usunięte, a
          wszystkie zgromadzone na nim środki przepadają nieodwracalnie.
        </AlertText>
        <DeleteButton
          background="red"
          type="button"
          noWrapper
          onClick={() => alert.open(this.getDeleteConfirmAlertConfig())}
        >
          Usuń konto*
        </DeleteButton>
      </StyledAccountSettingsDropDownContent>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  dispatchSubmitDelete(data) {
    return dispatch(submitDelete(data));
  },
  dispatchSubmitChangedProfile(profileData) {
    return dispatch(submitChangedProfile(profileData));
  },
  dispatchSubmitChangedPayPal(profileData) {
    return dispatch(submitChangedPayPal(profileData));
  },
  dispatchSubmitChangedSocialMediaLink(profileData) {
    return dispatch(submitChangedSocialMediaLink(profileData));
  },
  dispatchSubmitChangedContactName(profileData) {
    return dispatch(submitChangedContactName(profileData));
  },
  dispatchFetchProfile() {
    return dispatch(fetchProfile());
  },
});

export default connect(
  null,
  mapDispatchToProps,
)(PersonalData);
